import { ButtonContainer } from "nsitools-react";
import * as React from "react";
import { useParams } from "react-router";

import {
  EnveloppeProjetFinancementApi,
  EnveloppeProjetFinancementSearch,
  FcbEnveloppeProjetFinancementGridDto
} from "../../../../../../api";
import { EditButton, SearchTablePage, ViewButton } from "../../../../../../components";
import { useAbortableApiServiceFactory, useApiService, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { EnveloppePFDialog } from "./EnveloppePFDialog";

interface IEnveloppePFSearchProps {}

export const EnveloppePFSearch: React.FunctionComponent<IEnveloppePFSearchProps> = () => {
  const { t } = useTl();
  const { id } = useParams<{ id: string }>();
  const projetFinancementId = React.useMemo(() => +id, [id]);
  const api = useApiService(EnveloppeProjetFinancementApi);

  const [enveloppeIdSelected, setEnveloppeIdSelected] = React.useState(0);
  const [enveloppePopupOpened, setEnveloppePopupOpened] = React.useState(false);
  const [enveloppePopupEdit, setEnveloppePopupEdit] = React.useState(false);

  const enveloppeDialogOpen = React.useCallback(() => {
    setEnveloppePopupOpened(true);
  }, []);

  const enveloppeDialogClose = React.useCallback(() => {
    setEnveloppeIdSelected(0);
    setEnveloppePopupOpened(false);
  }, []);

  const editClick = React.useCallback((row: FcbEnveloppeProjetFinancementGridDto, editMode: boolean) => {
    setEnveloppeIdSelected(row.idenveloppeProjetFinancement);
    setEnveloppePopupEdit(editMode);
    setEnveloppePopupOpened(true);
  }, []);

  const onAddItem = React.useCallback(() => {
    setEnveloppeIdSelected(0);
    setEnveloppePopupEdit(true);
    enveloppeDialogOpen();
  }, [enveloppeDialogOpen]);

  const columns = React.useMemo(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: FcbEnveloppeProjetFinancementGridDto) => (
          <ButtonContainer>
            <ViewButton minimal={true} onClick={() => editClick(row, false)} />
            <EditButton minimal={true} onClick={() => editClick(row, true)} />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Code),
        fieldName: "idanneeScolaire"
      },
      {
        header: () => t(ETLCodes.EntiteCentre),
        fieldName: "centre"
      },
      {
        header: () => t(ETLCodes.HeureS1),
        fieldName: "heureS1"
      },
      {
        header: () => t(ETLCodes.HeureS2),
        fieldName: "heureS2"
      }
    ],
    [editClick, t]
  );

  const apiFactory = useAbortableApiServiceFactory(EnveloppeProjetFinancementApi);
  const lastAbortController = React.useRef<AbortController>();
  const searchFn = React.useCallback(
    (sObj?: EnveloppeProjetFinancementSearch) => {
      sObj.idprojetFinancement = projetFinancementId;
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.enveloppeProjetFinancementBaseSearch({ EnveloppeProjetFinancementSearch: sObj });
    },
    [apiFactory, projetFinancementId]
  );

  const getCriteriasFn = React.useCallback(
    () => api.enveloppeProjetFinancementGetSearchCriterias({ includeListsValues: true }),
    [api]
  );

  const EnvDialog = React.useCallback(
    () => (
      <EnveloppePFDialog
        isOpen={enveloppePopupOpened}
        enveloppeId={enveloppeIdSelected}
        onClose={enveloppeDialogClose}
        editMode={enveloppePopupEdit}
      />
    ),
    [enveloppeDialogClose, enveloppePopupEdit, enveloppePopupOpened, enveloppeIdSelected]
  );

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <>
      <SearchTablePage
        title={ETLCodes.EnveloppeProjetFinancement}
        withCard={false}
        getCriteriasFunction={getCriteriasFn}
        searchFunction={searchFn}
        onAbort={onAbort}
        columns={columns}
        addFunc={onAddItem}
        overrideListValues={{
          IdanneeScolaire: annee => annee.displayValue,
          Identite: centre => centre.displayValue
        }}
      />
      <EnvDialog />
    </>
  );
};
